import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  RefreshCw, 
  ArrowLeft,
  CreditCard,
  Receipt,
  Printer,
  CloudOff
} from 'lucide-react';
import { parsePaymentCallbackParams } from '@/hooks/useSmartPOS';
import { usePendingPayments } from '@/hooks/usePendingPayments';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { autoReconcilePayment } from '@/lib/cardReconciliationService';
import { useOfflineSyncQueue, isNetworkError } from '@/hooks/useOfflineSyncQueue';
import { useThermalPrinter } from '@/hooks/useThermalPrinter';

type CallbackStatus = 'loading' | 'success' | 'error' | 'not-found' | 'invalid';

export default function PaymentCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [paymentDetails, setPaymentDetails] = useState<{
    amount: number;
    nsu?: string;
    authorizationCode?: string;
    cardBrand?: string;
  } | null>(null);

  const { updatePendingPayment, findByTransactionId } = usePendingPayments();
  const { queueItem } = useOfflineSyncQueue();
  const { printReceipt, isNative } = useThermalPrinter();
  const [savedOffline, setSavedOffline] = useState(false);

  useEffect(() => {
    processCallback();
  }, [searchParams]);

  const processCallback = async () => {
    try {
      // Parse callback parameters
      const params = parsePaymentCallbackParams();
      
      if (!params) {
        setStatus('invalid');
        setErrorMessage('Parâmetros de retorno inválidos');
        return;
      }

      const { transactionId, status: paymentStatus, nsu, authorizationCode, cardBrand, cardLastDigits, errorMessage: paymentError } = params;

      // Find pending payment
      const pendingPayment = await findByTransactionId(transactionId);
      
      if (!pendingPayment) {
        setStatus('not-found');
        setErrorMessage('Transação não encontrada');
        return;
      }

      // Check if already processed
      if (pendingPayment.status !== 'pending' && pendingPayment.status !== 'processing') {
        // Already processed, show current status
        if (pendingPayment.status === 'success') {
          setStatus('success');
          setPaymentDetails({
            amount: Number(pendingPayment.amount),
            nsu: (pendingPayment.return_data as { nsu?: string })?.nsu,
            authorizationCode: (pendingPayment.return_data as { authorizationCode?: string })?.authorizationCode,
          });
        } else {
          setStatus('error');
          setErrorMessage(pendingPayment.error_message || 'Pagamento não concluído');
        }
        return;
      }

      if (paymentStatus === 'success') {
        // Update pending payment to success
        await updatePendingPayment.mutateAsync({
          transaction_id: transactionId,
          status: 'success',
          return_data: {
            nsu,
            authorizationCode,
            cardBrand,
            cardLastDigits,
          },
        });

        // If linked to a sale, add the payment
        if (pendingPayment.sale_id) {
          await addSalePayment(pendingPayment.sale_id, pendingPayment, {
            nsu,
            authorizationCode,
            cardBrand,
            cardLastDigits,
          });
        }

        setStatus('success');
        setPaymentDetails({
          amount: Number(pendingPayment.amount),
          nsu,
          authorizationCode,
          cardBrand,
        });

        toast({
          title: 'Pagamento confirmado! 🎉',
          description: 'O pagamento foi processado com sucesso.',
        });
      } else {
        // Payment failed or cancelled
        await updatePendingPayment.mutateAsync({
          transaction_id: transactionId,
          status: paymentStatus,
          error_message: paymentError,
        });

        setStatus('error');
        setErrorMessage(paymentError || 'Pagamento não concluído');
      }
    } catch (error) {
      console.error('Error processing payment callback:', error);
      
      // Check if it's a network error - queue for later sync
      if (isNetworkError(error)) {
        const params = parsePaymentCallbackParams();
        if (params && params.status === 'success') {
          queueItem({
            transactionId: params.transactionId,
            status: 'success',
            nsu: params.nsu,
            authorizationCode: params.authorizationCode,
            cardBrand: params.cardBrand,
            cardLastDigits: params.cardLastDigits,
            provider: 'unknown',
            amount: 0,
            paymentMethod: 'credit',
          });
          
          setSavedOffline(true);
          setStatus('success');
          setPaymentDetails({ amount: 0, nsu: params.nsu, authorizationCode: params.authorizationCode });
          
          toast({
            title: 'Pagamento salvo localmente',
            description: 'Será sincronizado quando a conexão retornar.',
            variant: 'default',
          });
          return;
        }
      }
      
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Erro ao processar retorno');
    }
  };

  const addSalePayment = async (
    saleId: string,
    pendingPayment: { amount: number; payment_method: string; provider: string },
    cardData: { nsu?: string; authorizationCode?: string; cardBrand?: string; cardLastDigits?: string }
  ) => {
    // Get company_id from sale
    const { data: sale } = await supabase
      .from('sales')
      .select('company_id')
      .eq('id', saleId)
      .single();

    if (!sale) throw new Error('Venda não encontrada');

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();

    // Add payment record
    const paymentMethod = pendingPayment.payment_method === 'credit' ? 'credit_card' : 'debit_card';
    const paymentDate = new Date().toISOString();
    
    const { data: paymentResult } = await supabase
      .from('sale_payments')
      .insert({
        sale_id: saleId,
        company_id: sale.company_id,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        payment_value: pendingPayment.amount,
        created_by: user?.id,
        notes: `${pendingPayment.provider.toUpperCase()} - NSU: ${cardData.nsu || 'N/A'}`,
        external_payment_id: cardData.authorizationCode,
        authorization_code: cardData.authorizationCode,
        nsu: cardData.nsu,
        card_brand: cardData.cardBrand,
        card_last_digits: cardData.cardLastDigits,
        terminal_provider: pendingPayment.provider,
        external_payment_status: 'approved',
      })
      .select('id')
      .single();

    // Conciliação automática com card_transactions
    if (paymentResult?.id && (cardData.nsu || cardData.authorizationCode)) {
      const reconciliationResult = await autoReconcilePayment(
        paymentResult.id,
        sale.company_id,
        {
          nsu: cardData.nsu,
          authorizationCode: cardData.authorizationCode,
          amount: pendingPayment.amount,
          paymentDate: paymentDate,
        },
        user?.id
      );

      if (reconciliationResult.success) {
        console.log(`[CardReconciliation] Auto-reconciliado: ${reconciliationResult.matchType}`);
      } else {
        console.log(`[CardReconciliation] Pendente: ${reconciliationResult.message}`);
      }
    }

    // Update sale payment status
    const { data: payments } = await supabase
      .from('sale_payments')
      .select('payment_value')
      .eq('sale_id', saleId);

    const totalPaid = payments?.reduce((sum, p) => sum + Number(p.payment_value), 0) || 0;

    const { data: saleData } = await supabase
      .from('sales')
      .select('total_value')
      .eq('id', saleId)
      .single();

    let paymentStatus: 'pending' | 'partial' | 'paid' = 'pending';
    if (totalPaid >= Number(saleData?.total_value)) {
      paymentStatus = 'paid';
    } else if (totalPaid > 0) {
      paymentStatus = 'partial';
    }

    await supabase
      .from('sales')
      .update({
        paid_value: totalPaid,
        payment_status: paymentStatus,
      })
      .eq('id', saleId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handleRetry = () => {
    navigate(-1);
  };

  const handleGoToSales = () => {
    navigate('/dashboard/sales');
  };

  const handleGoToPOS = () => {
    navigate('/pdv');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl">Retorno do Pagamento</CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <p className="text-muted-foreground">Processando retorno...</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              </div>
              
              <div className="text-center">
                <h2 className="text-2xl font-bold text-emerald-600">
                  Pagamento Confirmado! 🎉
                </h2>
                <p className="text-muted-foreground mt-1">
                  O pagamento foi processado com sucesso.
                </p>
              </div>

              {paymentDetails && (
                <div className="w-full space-y-3 p-4 bg-muted/50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Valor</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {formatCurrency(paymentDetails.amount)}
                    </span>
                  </div>
                  
                  {paymentDetails.cardBrand && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Bandeira</span>
                      <span className="font-medium">{paymentDetails.cardBrand}</span>
                    </div>
                  )}
                  
                  {paymentDetails.nsu && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">NSU</span>
                      <span className="font-mono text-sm">{paymentDetails.nsu}</span>
                    </div>
                  )}
                  
                  {paymentDetails.authorizationCode && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Autorização</span>
                      <span className="font-mono text-sm">{paymentDetails.authorizationCode}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="w-full space-y-2 pt-4">
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handleGoToPOS}
                >
                  <Receipt className="mr-2 h-5 w-5" />
                  Voltar ao PDV
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleGoToSales}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Ver Vendas
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {(status === 'error' || status === 'not-found' || status === 'invalid') && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              
              <div className="text-center">
                <h2 className="text-xl font-bold text-red-600">
                  Pagamento Não Concluído
                </h2>
                <p className="text-muted-foreground mt-1">
                  {errorMessage || 'O pagamento não foi processado.'}
                </p>
              </div>

              <div className="w-full space-y-2 pt-4">
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handleRetry}
                >
                  <RefreshCw className="mr-2 h-5 w-5" />
                  Tentar Novamente
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleGoToPOS}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar ao PDV
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
